import { RouteError } from './components/resilience';
import { installDialogShim } from './lib/dialogShim';
import { StrictMode, type ReactNode, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@fontsource-variable/outfit';
import '@fontsource-variable/fraunces';
import '@hutdrop/design-tokens/tokens.css';
import './styles/dashboard.css';
import './styles/catalog.css';
import './styles/native.css';
import './styles/legacy-gap.css';
import type { Permission } from '@hutdrop/shared';
import { StaffAuthProvider, useStaffAuth } from './lib/auth';
import { Shell } from './components/Shell';
import { LoginPage } from './pages/LoginPage';
import { OverviewPage } from './pages/OverviewPage';

// Pages load on demand (Phase 23): the first screen downloads only what it needs.
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })));
const ProductsPage = lazy(() => import('./pages/ProductsPage').then((m) => ({ default: m.ProductsPage })));
const ProductEditorPage = lazy(() => import('./pages/ProductEditorPage').then((m) => ({ default: m.ProductEditorPage })));
const LibraryPage = lazy(() => import('./pages/LibraryPage').then((m) => ({ default: m.LibraryPage })));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage').then((m) => ({ default: m.CategoriesPage })));
const InventoryPage = lazy(() => import('./pages/InventoryPage').then((m) => ({ default: m.InventoryPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const PromotionsPage = lazy(() => import('./pages/PromotionsPage').then((m) => ({ default: m.PromotionsPage })));
const PromotionEditorPage = lazy(() => import('./pages/PromotionEditorPage').then((m) => ({ default: m.PromotionEditorPage })));
const CouponEditorPage = lazy(() => import('./pages/CouponsPage').then((m) => ({ default: m.CouponEditorPage })));
const CouponsPage = lazy(() => import('./pages/CouponsPage').then((m) => ({ default: m.CouponsPage })));
const OrderScanPage = lazy(() => import('./pages/OrdersPage').then((m) => ({ default: m.OrderScanPage })));
const OrdersPage = lazy(() => import('./pages/OrdersPage').then((m) => ({ default: m.OrdersPage })));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })));
const CustomerDetailPage = lazy(() => import('./pages/CustomersPage').then((m) => ({ default: m.CustomerDetailPage })));
const CustomersPage = lazy(() => import('./pages/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const VipPage = lazy(() => import('./pages/VipPage').then((m) => ({ default: m.VipPage })));
const RewardsPage = lazy(() => import('./pages/RewardsPage').then((m) => ({ default: m.RewardsPage })));
const StoreInfoPage = lazy(() => import('./pages/StoreInfoPage').then((m) => ({ default: m.StoreInfoPage })));
const StaffPage = lazy(() => import('./pages/StaffPage').then((m) => ({ default: m.StaffPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 15_000 } } });

function Guarded({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { can } = useStaffAuth();
  return can(permission) ? children : <Navigate to="/" replace />;
}

const guard = (permission: Permission, element: ReactNode) => <Guarded permission={permission}>{element}</Guarded>;

const router = createBrowserRouter([
  {
    element: <Shell />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: guard('orders.view', <OverviewPage />) },
      { path: '/orders', element: guard('orders.view', <OrdersPage />) },
      { path: '/orders/scan', element: guard('orders.scan', <OrderScanPage />) },
      { path: '/orders/:id', element: guard('orders.view', <OrderDetailPage />) },
      { path: '/products', element: guard('products.view', <ProductsPage />) },
      { path: '/products/new', element: guard('products.edit', <ProductEditorPage />) },
      { path: '/products/categories', element: guard('products.edit', <CategoriesPage />) },
      { path: '/products/library', element: guard('products.edit', <LibraryPage />) },
      { path: '/products/:id', element: guard('products.view', <ProductEditorPage />) },
      { path: '/inventory', element: guard('products.view', <InventoryPage tab="stock" />) },
      { path: '/inventory/receive', element: guard('inventory.receive', <InventoryPage tab="receive" />) },
      { path: '/inventory/history', element: guard('inventory.history', <InventoryPage tab="history" />) },
      { path: '/inventory/bulk', element: guard('inventory.bulk', <InventoryPage tab="bulk" />) },
      { path: '/promotions', element: guard('promotions.manage', <PromotionsPage />) },
      { path: '/promotions/new', element: guard('promotions.manage', <PromotionEditorPage />) },
      { path: '/promotions/:id', element: guard('promotions.manage', <PromotionEditorPage />) },
      { path: '/coupons', element: guard('promotions.manage', <CouponsPage />) },
      { path: '/coupons/new', element: guard('promotions.manage', <CouponEditorPage />) },
      { path: '/coupons/:id', element: guard('promotions.manage', <CouponEditorPage />) },
      { path: '/rewards', element: guard('promotions.manage', <RewardsPage />) },
      { path: '/vip', element: guard('vip.manage', <VipPage />) },
      { path: '/customers', element: guard('customers.view', <CustomersPage />) },
      { path: '/customers/:id', element: guard('customers.view', <CustomerDetailPage />) },
      { path: '/reports', element: guard('reports.view', <ReportsPage />) },
      { path: '/audit', element: guard('audit.view', <AuditPage />) },
      { path: '/settings', element: guard('settings.manage', <SettingsPage />) },
      { path: '/staff', element: guard('staff.manage', <StaffPage />) },
      { path: '/store', element: guard('store.hours', <StoreInfoPage />) },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

function Root() {
  const { ready, session, staff, loadError, retry } = useStaffAuth();
  if (!ready) {
    return (
      <div className="boot" aria-busy="true">
        <span className="spinner" aria-label="Loading" />
      </div>
    );
  }
  if (session && loadError) {
    return (
      <div className="boot">
        <p className="alert" role="alert">
          {loadError.message}{' '}
          <button className="link-btn" onClick={retry}>
            Try again
          </button>
        </p>
      </div>
    );
  }
  if (!session || !staff) return <LoginPage />;
  return <RouterProvider router={router} />;
}

// Old iPads need a popup polyfill before anything renders; everywhere else this returns at once.
void installDialogShim()
  .catch(() => undefined)
  .then(() =>
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <StaffAuthProvider>
            <Root />
          </StaffAuthProvider>
        </QueryClientProvider>
      </StrictMode>,
    ),
  );
